"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Clock, AlertCircle, Loader, ChevronRight, Timer, FileText } from "lucide-react"
import { getQuizData } from "./api"
import { generatePresignedUrl } from "../postFileAws"
import { postAws } from "../aws"
import { motion, AnimatePresence } from "framer-motion"

export default function QuizDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [quiz, setQuiz] = useState(null)
  const [questionArr, setQuestionArr] = useState([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [evaluating, setEvaluating] = useState(false)
  const [studentId, setStudentId] = useState("")
  const [studentName, setStudentName] = useState("")
  const [questionImages, setQuestionImages] = useState({})
  const [timeLeft, setTimeLeft] = useState(0)
  const [totalTimeLeft, setTotalTimeLeft] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const organisationId = localStorage.getItem("organisationId")

  const [showStartDialog, setShowStartDialog] = useState(true)
  const [quizStarted, setQuizStarted] = useState(false)
  const [violationCount, setViolationCount] = useState(0)
  const [showViolationWarning, setShowViolationWarning] = useState(false)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [violationMessage, setViolationMessage] = useState("")

  const [showFullScreenWarning, setShowFullScreenWarning] = useState(false)
  const [fullScreenCountdown, setFullScreenCountdown] = useState(8)
  const [countdownInterval, setCountdownInterval] = useState(null)

  const enterFullScreen = async () => {
    const element = document.documentElement
    try {
      if (element.requestFullscreen) {
        await element.requestFullscreen()
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen()
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen()
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen()
      }
      setIsFullScreen(true)
      console.log("Entered fullscreen successfully")
    } catch (error) {
      console.error("Error entering fullscreen:", error)
      // Show error to user
      alert("Please allow fullscreen mode to continue with the quiz.")
    }
  }

  const handleFullScreenExit = () => {
    if (!quizStarted || submitting) return

    // Count as violation
    handleViolation("exit full screen")

    // Start 8-second countdown
    setShowFullScreenWarning(true)
    setFullScreenCountdown(8)

    const interval = setInterval(() => {
      setFullScreenCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          setShowFullScreenWarning(false)
          submitQuiz() // Auto-submit after 8 seconds
          return 0
        }
        return prev - 1
      })
    }, 1000)

    setCountdownInterval(interval)
  }

  const returnToFullScreen = async () => {
    // Clear countdown
    if (countdownInterval) {
      clearInterval(countdownInterval)
      setCountdownInterval(null)
    }
    setShowFullScreenWarning(false)
    setFullScreenCountdown(8)

    // Re-enter full screen
    try {
      await enterFullScreen()
      // Verify fullscreen is active after a short delay
      setTimeout(() => {
        const isFullScreenActive = !!(
          document.fullscreenElement ||
          document.mozFullScreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
        )

        if (!isFullScreenActive && !submitting) {
          console.log("Still not in fullscreen, showing warning again")
          handleFullScreenExit()
        }
      }, 1000)
    } catch (error) {
      console.error("Failed to return to fullscreen:", error)
      // If can't return to fullscreen and not submitting, continue countdown
      if (!submitting) {
        handleFullScreenExit()
      }
    }
  }

  const exitFullScreen = () => {
    try {
      // Check if document is actually in fullscreen before trying to exit
      const isCurrentlyFullScreen = !!(
        document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      )

      if (!isCurrentlyFullScreen) {
        console.log("Document is not in fullscreen, no need to exit")
        setIsFullScreen(false)
        return
      }

      // Try to exit fullscreen with proper error handling
      if (document.exitFullscreen) {
        document.exitFullscreen().catch((error) => {
          console.log("Error exiting fullscreen:", error)
        })
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
      }

      setIsFullScreen(false)
    } catch (error) {
      console.log("Error in exitFullScreen:", error)
      setIsFullScreen(false)
    }
  }
  // Add this useEffect after the existing ones
  useEffect(() => {
    if (!quizStarted || submitting) return

    // Check fullscreen status every 2 seconds
    const fullscreenChecker = setInterval(() => {
      const isCurrentlyFullScreen = !!(
        document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      )

      if (!isCurrentlyFullScreen && !showFullScreenWarning) {
        console.log("Periodic check: User not in fullscreen")
        handleFullScreenExit()
      }
    }, 2000)

    return () => clearInterval(fullscreenChecker)
  }, [quizStarted, submitting, showFullScreenWarning])
  const handleViolation = (type) => {
    if (violationCount >= 2) {
      // Third violation - auto submit
      setViolationMessage("Maximum attempts exceeded. Quiz will be auto-submitted.")
      setShowViolationWarning(true)
      setTimeout(() => {
        submitQuiz()
      }, 2000)
    } else {
      setViolationCount((prev) => prev + 1)
      setViolationMessage(
        `Warning ${violationCount + 1}/3: You cannot ${type}. ${2 - violationCount} chances remaining.`,
      )
      setShowViolationWarning(true)
    }
  }

  const startQuiz = async () => {
    setShowStartDialog(false)
    setQuizStarted(true)

    // Force fullscreen entry
    try {
      await enterFullScreen()
      // Wait a bit to ensure fullscreen is active
      setTimeout(() => {
        const isFullScreenActive = !!(
          document.fullscreenElement ||
          document.mozFullScreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
        )

        if (!isFullScreenActive) {
          console.log("Failed to enter fullscreen, trying again...")
          enterFullScreen()
        }
      }, 500)
    } catch (error) {
      console.error("Failed to enter fullscreen:", error)
    }

    // Add event listeners for violations
    const handleBeforeUnload = (e) => {
      e.preventDefault()
      e.returnValue = ""
      handleViolation("refresh or close the page")
      return ""
    }

    const handleVisibilityChange = () => {
      if (document.hidden && quizStarted && !submitting) {
        handleViolation("switch tabs or minimize window")
      }
    }

    const handleBlur = () => {
      if (quizStarted && !submitting) {
        handleViolation("leave the quiz window")
      }
    }

    const handleKeyDown = (e) => {
      // Prevent F11, Alt+Tab, Ctrl+Tab, etc.
      if (
        e.key === "F11" ||
        (e.altKey && e.key === "Tab") ||
        (e.ctrlKey && e.key === "Tab") ||
        (e.ctrlKey && e.key === "r") ||
        e.key === "F5" ||
        e.key === "Escape"
      ) {
        e.preventDefault()
        handleViolation("use keyboard shortcuts")
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    document.addEventListener("visibilitychange", handleVisibilityChange)
    window.addEventListener("blur", handleBlur)
    document.addEventListener("keydown", handleKeyDown)
    document.addEventListener("fullscreenchange", handleFullScreenChange)
    document.addEventListener("mozfullscreenchange", handleFullScreenChange)
    document.addEventListener("webkitfullscreenchange", handleFullScreenChange)
    document.addEventListener("msfullscreenchange", handleFullScreenChange)

    // Cleanup function
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      window.removeEventListener("blur", handleBlur)
      document.removeEventListener("keydown", handleKeyDown)
      document.removeEventListener("fullscreenchange", handleFullScreenChange)
      document.removeEventListener("mozfullscreenchange", handleFullScreenChange)
      document.removeEventListener("webkitfullscreenchange", handleFullScreenChange)
      document.removeEventListener("msfullscreenchange", handleFullScreenChange)
    }
  }

  const handleFullScreenChange = () => {
    const isCurrentlyFullScreen = !!(
      document.fullscreenElement ||
      document.mozFullScreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement
    )

    console.log(
      "Fullscreen change detected:",
      isCurrentlyFullScreen,
      "Quiz started:",
      quizStarted,
      "Submitting:",
      submitting,
    )

    setIsFullScreen(isCurrentlyFullScreen)

    // If user exits fullscreen during quiz (and we're not in the middle of submitting)
    if (!isCurrentlyFullScreen && quizStarted && !submitting && !showFullScreenWarning) {
      console.log("User exited fullscreen - triggering violation")
      handleFullScreenExit()
    }
  }

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        setLoading(true)
        const response = await getQuizData(id)
        if (response && response.statusCode === 200) {
          const quizData = JSON.parse(response.body).data
          setQuiz(quizData)
          setQuestionArr(quizData.quizQuestions || [])
          const timePerQuestion = Number.parseInt(quizData.timePerQuestion) || 30
          setTimeLeft(timePerQuestion)
          setTotalTimeLeft(timePerQuestion * quizData.quizQuestions.length)

          const imageUrls = {}
          for (const question of quizData.quizQuestions || []) {
            if (question.questionImageUrl) {
              try {
                const url = await generatePresignedUrl(question.questionImageUrl)
                imageUrls[question.questionImageUrl] = url
              } catch (error) {
                console.error("Error generating presigned URL:", error)
              }
            }
          }
          setQuestionImages(imageUrls)
        } else {
          throw new Error("Failed to fetch quiz data")
        }
      } catch (error) {
        console.error("Error fetching quiz:", error)
        setError("Failed to load quiz. Please try again later.")
      } finally {
        setLoading(false)
      }
    }

    function getStudentDataFromLocalStorage() {
      const studentKiId = localStorage.getItem("studentId")
      const studentData = JSON.parse(localStorage.getItem("studentData"))
      if (studentKiId) {
        setStudentId(studentKiId)
      }
      if (studentData && studentData.studentName) {
        setStudentName(studentData.studentName)
      }
    }

    fetchQuiz()
    getStudentDataFromLocalStorage()
  }, [id])

  useEffect(() => {
    if (!quiz || submitting) return

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          handleNextQuestion()
          return Number.parseInt(quiz.timePerQuestion) || 30
        }
        return prevTime - 1
      })
      setTotalTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          submitQuiz()
          return 0
        }
        return prevTime - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [quiz, submitting])

  const handleAnswerChange = (selectedAnswer) => {
    const updatedQuestions = [...questionArr]
    updatedQuestions[currentQuestionIndex].user_answer = selectedAnswer
    setQuestionArr(updatedQuestions)
  }

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questionArr.length - 1) {
      setCurrentQuestionIndex((prevIndex) => prevIndex + 1)
      setTimeLeft(Number.parseInt(quiz.timePerQuestion) || 30)
    } else {
      submitQuiz()
    }
  }

  const submitQuiz = async () => {
    setSubmitting(true)

    // Safely exit fullscreen
    try {
      exitFullScreen()
    } catch (error) {
      console.log("Error exiting fullscreen during submit:", error)
    }

    // Clear any active countdown intervals
    if (countdownInterval) {
      clearInterval(countdownInterval)
      setCountdownInterval(null)
    }

    const studentKiId = localStorage.getItem("studentId")
    const submission = {
      request: "submit-Quiz",
      body: {
        quizId: quiz._id,
        studentId: studentKiId,
        organisationId: organisationId,
        studentName: studentName,
        teacherId: quiz.teacherId,
        quizQuestions: questionArr.map((q) => ({
          question: q.question,
          answer: q.user_answer || "",
          maxMarks: q.maxMarks,
          correctAnswer: q.correctAnswer,
          questionImageUrl: q.questionImageUrl || null,
          questionImageUri: q.questionImageUri || null,
        })),
        timeSpent: questionArr.length * Number.parseInt(quiz.timePerQuestion) - totalTimeLeft,
        violationCount: violationCount, // Track violations in submission
      },
    }

    try {
      const response = await postAws("https://api-dev.aurait.ai/quizSubmission", "POST", submission)
      const responseData = JSON.parse(response.body)
      const submissionId = responseData.submissionId

      setSubmitting(false)
      setEvaluating(true)

      // Call evaluation API
      await evaluateQuiz(submissionId)
    } catch (err) {
      console.error(`Couldn't submit quiz: ${err}`)
      setSubmitting(false)
      setError("Failed to submit quiz. Please try again.")
    }
  }

  const evaluateQuiz = async (submissionId) => {
    try {
      const evalResponse = await postAws("https://api-dev.aurait.ai/quizEval", "POST", { id: submissionId })
      const evalData = JSON.parse(evalResponse.body)

      // You can handle the evaluation response here if needed
      console.log("Evaluation complete:", evalData)

      setEvaluating(false)
      // Redirect to the results page
      navigate(`/quiz/results/${submissionId}`)
    } catch (err) {
      console.error(`Couldn't evaluate quiz: ${err}`)
      setEvaluating(false)
      setError("Failed to evaluate quiz. Please check your results later.")
      // Still redirect to the results page in case of error
      navigate(`/quiz/results/${submissionId}`)
    }
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  const getAnsweredQuestionsCount = () => {
    return questionArr.filter((q) => q.user_answer).length
  }

  const getQuizStats = () => {
    const total = questionArr.length
    const answered = getAnsweredQuestionsCount()
    const remaining = total - answered
    return { total, answered, remaining }
  }

  const renderCurrentQuestion = () => {
    const question = questionArr[currentQuestionIndex]
    if (!question) return null

    const marks = question.maxMarks || 0

    return (
      <motion.div
        key={currentQuestionIndex}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
        className="w-full"
      >
        <div className="flex items-center mb-4">
          <div className="bg-blue-500 text-white rounded-full w-10 h-10 flex items-center justify-center mr-3 text-lg font-bold">
            {currentQuestionIndex + 1}
          </div>
          <h2 className="text-xl font-bold text-gray-800">Question</h2>
          <div className="flex items-center ml-auto">
            <div className="w-7 h-7 rounded-full  backdrop-blur-sm border border-white/30 flex items-center justify-center mr-2"></div>
            <span className=" text-xs mr-2">
              Question {currentQuestionIndex + 1} of {questionArr.length}
            </span>
            <span className=" text-xs mr-2">
              {getAnsweredQuestionsCount()}/{questionArr.length}
            </span>
            <div className="w-24 bg-gray-200 rounded-full h-1.5 mr-1">
              <div
                className="bg-blue-500 h-1.5 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${(timeLeft / (Number.parseInt(quiz.timePerQuestion) || 30)) * 100}%` }}
              ></div>
            </div>
            <span className="text-white text-xs">
              {Math.round((getAnsweredQuestionsCount() / questionArr.length) * 100)}%
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <span className="text-sm bg-blue-100 text-blue-800 px-4 py-1.5 rounded-full font-medium">
            {question.questionDifficulty?.toLowerCase() || "easy"}
          </span>
          <span className="text-sm bg-blue-100 text-blue-800 px-4 py-1.5 rounded-full font-medium">
            {marks} {marks === 1 ? "Mark" : "Marks"}
          </span>
        </div>

        <p className="text-lg text-gray-700 mb-6">{question.question}</p>

        {question.questionImageUrl && (
          <div className="mb-6">
            <img
              src={questionImages[question.questionImageUrl] || question.questionImageUrl}
              alt="Question Image"
              className="max-w-full h-auto rounded-lg mx-auto"
              onError={(e) => {
                e.target.onerror = null
                e.target.src = "/placeholder.svg"
              }}
            />
          </div>
        )}

        <div className="grid grid-cols-1 gap-3">
          {question.options.map((option, optionIndex) => (
            <div key={optionIndex} className="w-full">
              <input
                type="radio"
                id={`question-${currentQuestionIndex}-option-${optionIndex}`}
                name={`question-${currentQuestionIndex}`}
                value={option}
                checked={question.user_answer === option}
                onChange={() => handleAnswerChange(option)}
                className="hidden"
              />
              <label
                htmlFor={`question-${currentQuestionIndex}-option-${optionIndex}`}
                className={`block p-4 rounded-lg border transition-all duration-200 cursor-pointer ${
                  question.user_answer === option
                    ? "bg-blue-50 border-blue-500 text-blue-700"
                    : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center">
                  <div
                    className={`w-5 h-5 mr-3 rounded-full flex items-center justify-center border ${
                      question.user_answer === option ? "border-blue-500" : "border-gray-300"
                    }`}
                  >
                    {question.user_answer === option && <div className="w-3 h-3 rounded-full bg-blue-500"></div>}
                  </div>
                  <span>{option}</span>
                </div>
              </label>
            </div>
          ))}
        </div>
      </motion.div>
    )
  }

  // Show start dialog first
  if (showStartDialog && quiz) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-2xl max-w-md w-full mx-4 shadow-2xl">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Quiz Instructions</h2>
            <div className="text-left space-y-3 mb-6">
              <p className="text-gray-700">
                ⚠️ <strong>Important Rules:</strong>
              </p>
              <ul className="text-sm text-gray-600 space-y-2 list-disc list-inside">
                <li>You cannot go back once you start</li>
                <li>Do not refresh the page</li>
                <li>Do not switch tabs or minimize window</li>
                <li>Do not use keyboard shortcuts</li>
                <li>Quiz will be in full-screen mode</li>
              </ul>
              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                <p className="text-red-700 text-sm font-medium">
                  ⚠️ You have only 3 chances. After 3 violations, your quiz will be auto-submitted!
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate("/quiz")}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={startQuiz}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Start Quiz
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show violation warning
  if (showViolationWarning) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-2xl max-w-sm w-full mx-4 shadow-2xl">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-red-600 mb-3">Violation Detected!</h3>
            <p className="text-gray-700 mb-4">{violationMessage}</p>
            <button
              onClick={() => setShowViolationWarning(false)}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Continue Quiz
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Show full screen exit warning with countdown
  if (showFullScreenWarning) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-2xl max-w-md w-full mx-4 shadow-2xl text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>

          {/* Countdown Circle */}
          <div className="relative w-24 h-24 mx-auto mb-6">
            <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" stroke="#fee2e2" strokeWidth="8" fill="none" />
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="#dc2626"
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 45}`}
                strokeDashoffset={`${2 * Math.PI * 45 * (1 - fullScreenCountdown / 8)}`}
                className="transition-all duration-1000 ease-linear"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-3xl font-bold text-red-600">{fullScreenCountdown}</span>
            </div>
          </div>

          <h3 className="text-xl font-bold text-red-600 mb-4">Full Screen Required!</h3>
          <p className="text-gray-700 mb-6">
            Quiz will be auto-submitted in <span className="font-bold text-red-600">{fullScreenCountdown} seconds</span>{" "}
            if you don't return to full screen.
          </p>

          <div className="space-y-3">
            <button
              onClick={returnToFullScreen}
              className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-bold text-lg"
            >
              Return to Full Screen
            </button>
            <p className="text-xs text-gray-500">This counts as violation {violationCount}/3</p>
          </div>
        </div>
      </div>
    )
  }

  if (loading || submitting || evaluating) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center max-w-sm w-full mx-4">
          <Loader className="w-12 h-12 text-blue-500 animate-spin mb-4" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            {loading ? "Loading Quiz" : submitting ? "Submitting Quiz" : "Evaluating Quiz"}
          </h3>
          <p className="text-gray-600 text-center">
            {loading
              ? "Please wait while we prepare your quiz..."
              : submitting
                ? "Your answers are being submitted..."
                : "Your quiz is being evaluated..."}
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4">
        <div className="bg-white p-6 rounded-lg shadow-md text-center max-w-sm w-full">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4 mx-auto" />
          <h2 className="text-xl font-bold text-gray-800 mb-3">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate("/quiz")}
            className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-300 font-medium"
          >
            Back to Quizzes
          </button>
        </div>
      </div>
    )
  }

  if (!quiz) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4">
        <div className="bg-white p-6 rounded-lg shadow-md text-center max-w-sm w-full">
          <AlertCircle className="w-12 h-12 text-yellow-500 mb-4 mx-auto" />
          <h2 className="text-xl font-bold text-gray-800 mb-3">Quiz Not Found</h2>
          <p className="text-gray-600 mb-6">The requested quiz could not be found.</p>
          <button
            onClick={() => navigate("/quiz")}
            className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-300 font-medium"
          >
            Back to Quizzes
          </button>
        </div>
      </div>
    )
  }

  const quizStats = getQuizStats()

  // Full-screen quiz layout
  return (
    <div
      className={`${isFullScreen ? "fixed inset-0 z-50" : "min-h-screen"} bg-gradient-to-br from-blue-50 to-indigo-100 overflow-hidden`}
    >
      {/* Violation count indicator */}
      {violationCount > 0 && (
        <div className="fixed top-4 right-4 z-40 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
          Violations: {violationCount}/3
        </div>
      )}

      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-4xl">
          {/* Main Content Card - Centered */}
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
            {/* Quiz Header - Single Row */}
            <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-4 py-4">
              {/* Desktop Layout - SINGLE ROW */}
              <div className="flex items-center justify-between">
                {/* Left section: Quiz info */}
                <div className="flex items-center gap-4">
                  <h1 className="text-lg font-bold text-white">{quiz.quizTitle}</h1>
                  <div className="flex items-center gap-2">
                    <div className="inline-flex items-center gap-1 border border-white/30 backdrop-blur-sm rounded-md px-2 py-1 text-white text-xs font-medium bg-white/10">
                      <FileText className="w-3 h-3 opacity-80" />
                      <span>
                        Q: {currentQuestionIndex + 1}/{questionArr.length}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right section: Timer and progress */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full border border-white/30">
                      <Timer className="w-4 h-4 mr-1" />
                      <span className="text-sm text-white font-bold">{timeLeft}s</span>
                    </div>
                    <div className="flex items-center bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full border border-white/30">
                      <Clock className="w-4 h-4 mr-1" />
                      <span className="text-sm font-medium text-white">{formatTime(totalTimeLeft)}</span>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="flex items-center">
                    <span className="text-white text-sm mr-2">
                      {getAnsweredQuestionsCount()}/{questionArr.length}
                    </span>
                    <div className="w-24 bg-white/20 rounded-full h-2">
                      <div
                        className="bg-white h-2 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${(getAnsweredQuestionsCount() / questionArr.length) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Question content - Centered and larger */}
            <div className="p-8 bg-white min-h-[500px] flex items-center justify-center">
              <div className="w-full max-w-3xl">
                <AnimatePresence mode="wait">{renderCurrentQuestion()}</AnimatePresence>
              </div>
            </div>

            {/* Footer - Centered */}
            <div className="bg-white px-6 py-4 border-t flex justify-center items-center">
              <div className="flex items-center gap-6">
                <div className="text-sm text-gray-600 font-medium flex items-center">
                  <Timer className="w-4 h-4 mr-2 text-blue-500" />
                  {currentQuestionIndex === questionArr.length - 1 ? (
                    <span className="text-red-600 font-bold">Final question - Submit when ready</span>
                  ) : (
                    <span>Auto-advance in {timeLeft}s</span>
                  )}
                </div>

                {currentQuestionIndex === questionArr.length - 1 ? (
                  <button
                    onClick={submitQuiz}
                    disabled={submitting || evaluating}
                    className={`bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-lg transition duration-300 ease-in-out focus:outline-none shadow-lg ${
                      (submitting || evaluating) && "opacity-50 cursor-not-allowed"
                    }`}
                  >
                    {submitting || evaluating ? (
                      <span className="flex items-center justify-center">
                        <Clock className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
                        {submitting ? "Submitting..." : "Evaluating..."}
                      </span>
                    ) : (
                      <span>Submit Quiz</span>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleNextQuestion}
                    className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-8 rounded-lg transition duration-300 ease-in-out flex items-center justify-center focus:outline-none shadow-lg"
                  >
                    Next Question
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
